/**
 * Shopper searches as `/v1` requests (design
 * docs/shop-agent/shop-agent-platform-v1-search-migration.md §6, Example 2).
 *
 * The results page, browse menu, catalog pages and brand grids send
 * `POST /shop-agent/search/products` a `/v1` search body, naming their switch
 * `surface`, and render the page it answers with. Their search state is a
 * `ResultsSearchState`: the request, the `effectiveQuery` it last ran as and
 * the browse-menu node it belongs to.
 *  - The sidebar and chips show `shownFilters(state)`: what ran, each facet
 *    keeping its `source`, or before anything ran the request's own filters.
 *  - The shopper edits those filters; `nextShopperSearch` turns the edit into
 *    request fields. An understood facet they removed goes to
 *    `omit_generated_facets` and stays omitted while it is not theirs; a
 *    facet they set or changed (stamped `caller`) goes to `facets` and is
 *    never relaxed away; their exclusions go to
 *    `exclude_facets`; price and brand-quality bounds and style brands go to
 *    their own fields.
 *  - A browse (no `q`: a menu node, a catalog page, a brand grid) has nothing
 *    understood, so every facet it shows is its request's.
 *  - "Load more" sends the same body with `cursor`, and the page keeps
 *    de-duplicating items by `uuid`.
 */

import type { Product } from "@/lib/product-utils";
import { applyPriceFilters } from "@/lib/results-page-utils";

import type {
  ProgrammaticProductSearchEffectiveFacet,
  ProgrammaticProductSearchEffectiveQuery,
  Facet,
  SearchFacet,
  ShopAgentSearchIntent,
  ShopAgentSearchProductsData,
  ShopperSearchRequest,
} from "../../openapi/shop_agent/client/types.gen";

/** Results per page the results page asks for (the route's maximum). */
export const RESULTS_PAGE_LIMIT = 100;

/** The route's bound on `similar_to_brands`. */
const MAX_SIMILAR_TO_BRANDS = 10;

/** The shopper surfaces whose switch serves a search on the shopper route. */
export type ShopperSearchSurface = NonNullable<
  NonNullable<ShopAgentSearchProductsData["query"]>["surface"]
>;

/** A search on the shopper route: the body it sends, and what it last ran as. */
export interface ResultsSearchState {
  /** The body without `cursor`; "Load more" sends it again with one. */
  request: ShopperSearchRequest;
  /** What the request last ran as; null until it has run. */
  effectiveQuery: ProgrammaticProductSearchEffectiveQuery | null;
  /** The browse-menu node the search belongs to, for the breadcrumb and Save search. */
  browseMenuUuid: string | null;
  /** The surface it runs as; absent for `results_page`. */
  surface?: ShopperSearchSurface;
  /**
   * Understood facet names the shopper removed. A caller facet of the same
   * name replaces the omission while it is set; removing it omits the name
   * again. Absent until the shopper removes one, when the request's
   * omissions stand.
   */
  omitted?: string[];
}

/**
 * The filters a search shows the shopper and they edit: the sidebar, chips,
 * "clear filters" and URL state read and write these. Each facet keeps its
 * `source`; `nextShopperSearch` maps an edit onto the request.
 */
export interface ShopperFilters {
  facets?: Facet[] | null;
  exclude_facets?: Facet[] | null;
  price_min?: number | null;
  price_max?: number | null;
  brand_quality_min?: number | null;
  brand_quality_max?: number | null;
  similar_to_brands?: string[] | null;
}

/**
 * A query chat metadata holds without an agent intent (a curated rail's
 * stored query): words, filters and the style tuning `/v1` has no field for.
 */
export interface StoredSearchQuery extends ShopperFilters {
  text?: string | null;
  limit?: number;
  retrieval_embedding_columns?: string[] | null;
  ranking_embedding_columns?: string[] | null;
}

/** A search state for `request` that has not run yet. */
export function resultsSearchState(
  request: ShopperSearchRequest,
  options: { browseMenuUuid?: string | null; surface?: ShopperSearchSurface } = {}
): ResultsSearchState {
  return {
    request,
    effectiveQuery: null,
    browseMenuUuid: options.browseMenuUuid ?? null,
    ...(options.surface ? { surface: options.surface } : {}),
  };
}

/** The words a search shows as its heading: what ran, else what it asks for. */
export function searchText(state: Pick<ResultsSearchState, "request" | "effectiveQuery">): string {
  return state.effectiveQuery?.text ?? state.request.q ?? "";
}

/** The chat agent's search, as a request. Null when it carries no words. */
export function shopperRequestFromIntent(
  intent: Pick<ShopAgentSearchIntent, "q" | "facets"> | null | undefined,
  options: { limit?: number } = {}
): ShopperSearchRequest | null {
  const q = intent?.q?.trim();
  if (!q) return null;
  const facets = mergeFacets(intent?.facets ?? []);
  return {
    q,
    limit: options.limit ?? RESULTS_PAGE_LIMIT,
    ...(facets.length > 0 ? { facets } : {}),
  };
}

/**
 * A stored search query as a request: `text` becomes `q`, filters the shopper
 * set become `facets` and `exclude_facets`, the bounds and style brands keep
 * their fields, and inferred facets are dropped because `q` is understood
 * again. Null for a query that was not understood from text: a browse (no
 * text), a style-tuned search (embedding columns), or one whose every facet
 * the caller set (brand navigation); those keep running as stored.
 */
export function shopperRequestFromStoredQuery(
  stored: StoredSearchQuery | null | undefined
): ShopperSearchRequest | null {
  const q = stored?.text?.trim();
  if (!stored || !q) return null;
  if (stored.retrieval_embedding_columns?.length || stored.ranking_embedding_columns?.length) {
    return null;
  }
  const storedFacets = stored.facets ?? [];
  if (storedFacets.length > 0 && storedFacets.every((facet) => facet.source === "caller")) {
    return null;
  }

  const request: ShopperSearchRequest = {
    q,
    limit: Math.min(stored.limit || RESULTS_PAGE_LIMIT, RESULTS_PAGE_LIMIT),
  };
  const facets = callerFacets(storedFacets);
  if (facets.length > 0) request.facets = facets;
  const excludeFacets = callerFacets(stored.exclude_facets);
  if (excludeFacets.length > 0) request.exclude_facets = excludeFacets;
  const prices = applyPriceFilters({}, stored.price_min, stored.price_max);
  if (prices.price_min != null) request.price_min = prices.price_min;
  if (prices.price_max != null) request.price_max = prices.price_max;
  if (stored.brand_quality_min != null) request.brand_quality_min = stored.brand_quality_min;
  if (stored.brand_quality_max != null) request.brand_quality_max = stored.brand_quality_max;
  const brands = styleBrands(stored.similar_to_brands);
  if (brands.length > 0) request.similar_to_brands = brands;
  return request;
}

/**
 * A brand page's product grid as a `/v1` browse: the brand as a caller
 * `brand_name` facet, plus any category facets (design Example 2).
 */
export function brandGridRequest(
  brandName: string,
  limit: number,
  facets: SearchFacet[] = []
): ShopperSearchRequest {
  return { facets: [{ name: "brand_name", values: [brandName] }, ...facets], limit };
}

/**
 * Any stored search query as a request: its text understood as `q`, or a
 * browse of its filters when it has no text or every facet is the caller's.
 * A style-tuned query (embedding columns) keeps its text and filters and
 * loses the style tuning, which `/v1` has no field for.
 */
export function shopperRequestFromQuery(
  stored: StoredSearchQuery,
  options: { limit: number }
): ShopperSearchRequest {
  const understood = shopperRequestFromStoredQuery({
    ...stored,
    retrieval_embedding_columns: undefined,
    ranking_embedding_columns: undefined,
  });
  return understood
    ? { ...understood, limit: options.limit }
    : browseRequestFromFilters(stored, options);
}

/**
 * A browse with `filters`: no `q`, every facet and exclusion as the
 * request's own, and the bounds and style brands in their fields. `catalogs`
 * scopes a catalog page.
 */
export function browseRequestFromFilters(
  filters: ShopperFilters,
  options: { limit: number; catalogs?: string[] }
): ShopperSearchRequest {
  const request: ShopperSearchRequest = { limit: options.limit };
  const facets = mergeFacets(filters.facets ?? []);
  if (facets.length > 0) request.facets = facets;
  const excludeFacets = mergeFacets(filters.exclude_facets ?? []);
  if (excludeFacets.length > 0) request.exclude_facets = excludeFacets;
  const prices = applyPriceFilters({}, filters.price_min, filters.price_max);
  if (prices.price_min != null) request.price_min = prices.price_min;
  if (prices.price_max != null) request.price_max = prices.price_max;
  if (filters.brand_quality_min != null) request.brand_quality_min = filters.brand_quality_min;
  if (filters.brand_quality_max != null) request.brand_quality_max = filters.brand_quality_max;
  const brands = styleBrands(filters.similar_to_brands);
  if (brands.length > 0) request.similar_to_brands = brands;
  if (options.catalogs?.length) request.catalogs = [...options.catalogs];
  return request;
}

/**
 * The filters the sidebar and chips show for `state`: what it last ran as,
 * else its request's own filters. A facet keeps its `source`: `caller` for
 * the request's own, `generated` for one understood from `q`.
 */
export function shownFilters(
  state: Pick<ResultsSearchState, "request" | "effectiveQuery">
): ShopperFilters {
  const ran = ranAs(state);
  const filters: ShopperFilters = {};
  const facets = (ran.facets ?? []).map(facetFromEffective);
  if (facets.length > 0) filters.facets = facets;
  const exclusions = (ran.excludeFacets ?? []).map(facetFromEffective);
  if (exclusions.length > 0) filters.exclude_facets = exclusions;
  if (ran.brandQualityMin != null) filters.brand_quality_min = ran.brandQualityMin;
  if (ran.brandQualityMax != null) filters.brand_quality_max = ran.brandQualityMax;
  if (ran.similarToBrands?.length) filters.similar_to_brands = [...ran.similarToBrands];
  return applyPriceFilters(filters, ran.priceMin, ran.priceMax);
}

/**
 * The request for `filters` after the shopper edited the filters `state`
 * shows, and the understood names they have removed so far. Fields the edit
 * did not touch keep their value in `state.request`. `/v1` cannot widen an
 * understood price or clear understood style brands, so dropping one of
 * those only drops the shopper's own value.
 */
export function nextShopperSearch(
  state: Pick<ResultsSearchState, "request" | "effectiveQuery" | "omitted">,
  filters: ShopperFilters
): Pick<ResultsSearchState, "request" | "omitted"> {
  const { request } = state;
  const effectiveQuery = ranAs(state);
  const {
    facets: _facets,
    exclude_facets: _excludeFacets,
    omit_generated_facets: _omit,
    price_min: _priceMin,
    price_max: _priceMax,
    brand_quality_min: _brandQualityMin,
    brand_quality_max: _brandQualityMax,
    similar_to_brands: _similarToBrands,
    cursor: _cursor,
    ...kept
  } = request;
  const next: ShopperSearchRequest = { ...kept };
  const understood = Boolean(request.q) && request.understand !== false;

  // Only an understood search has facets that are not the request's own.
  const facets = understood ? callerFacets(filters.facets) : mergeFacets(filters.facets ?? []);
  if (facets.length > 0) next.facets = facets;
  const excludeFacets = understood
    ? callerFacets(filters.exclude_facets)
    : mergeFacets(filters.exclude_facets ?? []);
  if (excludeFacets.length > 0) next.exclude_facets = excludeFacets;

  const omitted = new Set(state.omitted ?? request.omit_generated_facets ?? []);
  if (understood) {
    const shown = new Set((filters.facets ?? []).map((facet) => String(facet.name)));
    for (const facet of effectiveQuery.facets ?? []) {
      if (facet.source !== "caller" && !shown.has(facet.name)) omitted.add(facet.name);
    }
    const callerNames = new Set(facets.map((facet) => facet.name));
    const omit = [...omitted].filter((name) => !callerNames.has(name)).sort();
    if (omit.length > 0) next.omit_generated_facets = omit;
  }

  const priceMin = editedBound(filters.price_min, effectiveQuery.priceMin, request.price_min);
  if (priceMin != null) next.price_min = priceMin;
  const priceMax = editedBound(filters.price_max, effectiveQuery.priceMax, request.price_max);
  if (priceMax != null) next.price_max = priceMax;
  const qualityMin = editedBound(
    filters.brand_quality_min,
    effectiveQuery.brandQualityMin,
    request.brand_quality_min
  );
  if (qualityMin != null) next.brand_quality_min = qualityMin;
  const qualityMax = editedBound(
    filters.brand_quality_max,
    effectiveQuery.brandQualityMax,
    request.brand_quality_max
  );
  if (qualityMax != null) next.brand_quality_max = qualityMax;

  const shownBrands = styleBrands(filters.similar_to_brands);
  const ranBrands = styleBrands(effectiveQuery.similarToBrands);
  const brands = sameValues(shownBrands, ranBrands)
    ? (request.similar_to_brands ?? [])
    : shownBrands;
  if (brands.length > 0) next.similar_to_brands = brands;

  return { request: next, omitted: [...omitted].sort() };
}

/** `existing` followed by the items of `page` whose `uuid` it does not hold yet. */
export function appendUnseenProducts(existing: Product[], page: Product[]): Product[] {
  const seen = new Set(existing.map((product) => product.uuid));
  const added: Product[] = [];
  for (const product of page) {
    if (seen.has(product.uuid)) continue;
    seen.add(product.uuid);
    added.push(product);
  }
  return [...existing, ...added];
}

/**
 * What `state` last ran as; before it has run, its request read the same way
 * (every facet the request's own).
 */
function ranAs(
  state: Pick<ResultsSearchState, "request" | "effectiveQuery">
): ProgrammaticProductSearchEffectiveQuery {
  if (state.effectiveQuery) return state.effectiveQuery;
  const { request } = state;
  const caller = (facets: SearchFacet[] | null | undefined) =>
    (facets ?? []).map(({ name, values }) => ({ name, values: [...values], source: "caller" }));
  return {
    text: request.q ?? "",
    understood: false,
    facets: caller(request.facets),
    excludeFacets: caller(request.exclude_facets),
    priceMin: request.price_min ?? null,
    priceMax: request.price_max ?? null,
    brandQualityMin: request.brand_quality_min ?? null,
    brandQualityMax: request.brand_quality_max ?? null,
    similarToBrands: request.similar_to_brands ?? null,
  };
}

function facetFromEffective(facet: ProgrammaticProductSearchEffectiveFacet): Facet {
  return {
    name: facet.name,
    values: [...facet.values],
    source: facet.source === "caller" ? "caller" : "generated",
    stage: null,
  };
}

/** The `caller` facets, one per name. */
function callerFacets(facets: readonly Facet[] | null | undefined): SearchFacet[] {
  return mergeFacets((facets ?? []).filter((facet) => facet.source === "caller"));
}

function mergeFacets(facets: readonly { name: string; values: string[] }[]): SearchFacet[] {
  const byName = new Map<string, string[]>();
  for (const facet of facets) {
    const name = String(facet.name);
    const values = byName.get(name) ?? [];
    for (const value of facet.values) if (value && !values.includes(value)) values.push(value);
    byName.set(name, values);
  }
  return [...byName.entries()]
    .filter(([, values]) => values.length > 0)
    .map(([name, values]) => ({ name, values }));
}

/** The shopper's bound when they changed what ran, else the one already sent. */
function editedBound(
  shown: number | null | undefined,
  ran: number | null | undefined,
  sent: number | null | undefined
): number | null {
  return (shown ?? null) === (ran ?? null) ? (sent ?? null) : (shown ?? null);
}

function styleBrands(brands: readonly string[] | null | undefined): string[] {
  return (brands ?? [])
    .map((brand) => brand.trim())
    .filter(Boolean)
    .slice(0, MAX_SIMILAR_TO_BRANDS);
}

function sameValues(a: readonly string[], b: readonly string[]): boolean {
  const left = [...a].map((value) => value.toLowerCase()).sort();
  const right = [...b].map((value) => value.toLowerCase()).sort();
  return left.length === right.length && left.every((value, i) => value === right[i]);
}
